<template>
  <div class="itm" :class="isDark ? 'itm--dark' : 'itm--light'">
    <div class="itm-ambient" aria-hidden="true"></div>

    <!-- Header -->
    <header class="itm-header">
      <div class="itm-header__left">
        <img src="@/assets/logogruas.png" alt="Logo" class="itm-logo" @click="$router.push('/main-panel')" />
        <div>
          <h1 class="itm-title">Monitor de Infraestructura TI</h1>
          <p class="itm-sub">
            <span class="itm-live-dot" :class="{ 'itm-live-dot--on': wsStatus === 'open' }"></span>
            {{ wsStatus === 'open' ? 'VM en vivo' : 'VM en vivo desconectado' }}
            · Actualizado hace {{ secondsSinceUpdate }}s
          </p>
        </div>
      </div>
      <div class="itm-header__right">
        <div class="itm-clock" v-for="clock in clockCards" :key="clock.label">
          <span class="itm-clock__label">{{ clock.label }}</span>
          <span class="itm-clock__time">{{ clock.time }}</span>
          <span class="itm-clock__date">{{ clock.date }}</span>
        </div>
        <button class="itm-btn itm-btn--icon" @click="toggleTheme" :title="isDark ? 'Modo claro' : 'Modo oscuro'">
          <svg v-if="isDark" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
        </button>
        <button class="itm-btn itm-btn--icon" :class="{ 'itm-btn--spinning': loading }" @click="fetchAll" :disabled="loading" title="Refrescar">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
          </svg>
        </button>
        <button class="itm-btn" @click="$router.back()">Volver</button>
      </div>
    </header>

    <div v-if="error" class="itm-error">{{ error }}</div>

    <!-- Status tiles -->
    <section class="itm-tiles itm-rise" style="--rise: 0">
      <div v-for="tile in statusTiles" :key="tile.key" class="itm-tile" :class="`itm-tile--${tile.status}`">
        <span class="itm-tile__name">{{ tile.name }}</span>
        <span class="itm-tile__value">{{ tile.value }}</span>
        <span class="itm-tile__detail">{{ tile.detail }}</span>
      </div>
    </section>

    <!-- VM -->
    <section class="itm-section itm-rise" style="--rise: 1">
      <h2 class="itm-section__title">Servidor (Azure VM)</h2>
      <div class="itm-grid itm-grid--vm">
        <div class="itm-card">
          <h3>CPU</h3>
          <v-chart :option="cpuGaugeOption" autoresize class="itm-gauge" />
        </div>
        <div class="itm-card">
          <h3>Memoria</h3>
          <v-chart :option="memGaugeOption" autoresize class="itm-gauge" />
          <p class="itm-muted itm-center" v-if="vm.available">
            {{ vm.memory_used_gb }} / {{ vm.memory_total_gb }} GB
          </p>
        </div>
        <div class="itm-card itm-card--wide">
          <h3>En vivo (CPU / Memoria %)</h3>
          <v-chart :option="liveChartOption" autoresize class="itm-chart" />
        </div>
        <div class="itm-card">
          <h3>Discos</h3>
          <div v-for="disk in vm.disks || []" :key="disk.mount" class="itm-disk">
            <div class="itm-disk__head">
              <span class="itm-mono">{{ disk.mount }}</span>
              <span>{{ disk.used_gb }} / {{ disk.total_gb }} GB</span>
            </div>
            <div class="itm-bar">
              <div class="itm-bar__fill" :class="barClass(disk.percent)" :style="{ width: disk.percent + '%' }"></div>
            </div>
          </div>
          <p v-if="!(vm.disks || []).length" class="itm-muted">Sin datos</p>
        </div>
        <div class="itm-card">
          <h3>Sistema</h3>
          <dl class="itm-kv">
            <div><dt>Uptime</dt><dd class="itm-mono">{{ uptimeText }}</dd></div>
            <div><dt>Load avg (1/5/15m)</dt><dd class="itm-mono">{{ loadAvgText }}</dd></div>
            <div><dt>CPUs</dt><dd>{{ vm.cpu_count ?? '—' }}</dd></div>
            <div><dt>Swap</dt><dd>{{ vm.swap_percent != null ? vm.swap_percent + '%' : '—' }}</dd></div>
          </dl>
        </div>
        <div class="itm-card itm-card--wide">
          <h3>Últimas {{ historyHours }}h (muestras cada {{ Math.round(sampleIntervalSeconds / 60) }} min)</h3>
          <v-chart :option="vmHistoryOption" autoresize class="itm-chart" />
        </div>
      </div>
    </section>

    <!-- Devices -->
    <section class="itm-section itm-rise" style="--rise: 2">
      <h2 class="itm-section__title">Dispositivos</h2>
      <div class="itm-grid itm-grid--devices">
        <div v-for="device in devices" :key="device.key" class="itm-card">
          <div class="itm-device__head">
            <h3>{{ device.label }}</h3>
            <span class="itm-dot" :class="device.reachable ? 'itm-dot--ok' : 'itm-dot--down'"></span>
          </div>
          <dl class="itm-kv">
            <div><dt>IP</dt><dd class="itm-mono">{{ device.ip }}:{{ device.port }}</dd></div>
            <div><dt>Estado</dt><dd :class="device.reachable ? 'itm-ok' : 'itm-down'">
              {{ device.reachable ? 'Conectado' : 'Sin conexión' }}
            </dd></div>
            <div><dt>Latencia</dt><dd>{{ device.latency_ms != null ? device.latency_ms + ' ms' : '—' }}</dd></div>
            <div><dt>Última sincronización</dt><dd>{{ formatDateTime(device.sync?.last_sync_at) }}</dd></div>
            <div v-if="device.sync?.last_error"><dt>Último error</dt>
              <dd class="itm-down itm-small">{{ device.sync.last_error }}</dd></div>
          </dl>
          <h4 class="itm-squares-title">Disponibilidad ({{ historyHours }}h)</h4>
          <HealthSquares :slots="deviceSquares[device.key] || []" />
        </div>
      </div>
    </section>

    <!-- API -->
    <section class="itm-section itm-rise" style="--rise: 3">
      <h2 class="itm-section__title">API</h2>
      <div class="itm-grid itm-grid--api">
        <div class="itm-card">
          <h3>Última hora</h3>
          <dl class="itm-kv">
            <div><dt>Peticiones</dt><dd>{{ api.requests ?? '—' }}</dd></div>
            <div><dt>Por minuto</dt><dd>{{ api.requests_per_minute ?? '—' }}</dd></div>
            <div><dt>Latencia promedio</dt><dd>{{ api.avg_ms != null ? api.avg_ms + ' ms' : '—' }}</dd></div>
            <div><dt>Latencia máxima</dt><dd>{{ api.max_ms != null ? api.max_ms + ' ms' : '—' }}</dd></div>
            <div><dt>Errores 5xx</dt><dd :class="{ 'itm-down': api.errors > 0 }">{{ api.errors ?? '—' }}</dd></div>
          </dl>
          <h4 class="itm-squares-title">Salud por bloque de {{ apiSliceMinutes }} min ({{ historyHours }}h)</h4>
          <HealthSquares :slots="apiSquares" show-legend />
        </div>
        <div class="itm-card itm-card--wide">
          <h3>Latencia y tráfico ({{ historyHours }}h)</h3>
          <v-chart :option="apiHistoryOption" autoresize class="itm-chart itm-chart--tall" />
        </div>
        <div class="itm-card itm-card--full">
          <h3>Endpoints con más tráfico (última hora)</h3>
          <table class="itm-table">
            <thead>
              <tr><th>Método</th><th>Endpoint</th><th>Peticiones</th><th>Promedio</th><th>Máximo</th><th>Errores</th></tr>
            </thead>
            <tbody>
              <tr v-for="row in api.top_endpoints || []" :key="row.method + row.endpoint">
                <td><span class="itm-method">{{ row.method }}</span></td>
                <td class="itm-mono">{{ row.endpoint }}</td>
                <td>{{ row.count }}</td>
                <td>{{ row.avg_ms }} ms</td>
                <td>{{ row.max_ms }} ms</td>
                <td :class="{ 'itm-down': row.errors > 0 }">{{ row.errors }}</td>
              </tr>
              <tr v-if="!(api.top_endpoints || []).length">
                <td colspan="6" class="itm-muted">Sin datos todavía — el muestreador llena esta tabla.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="itm-card itm-card--full">
          <h3>Diagnóstico de latencia (última hora)</h3>
          <div class="itm-duo">
            <div>
              <h4 class="itm-squares-title">Más lentos (latencia promedio)</h4>
              <table class="itm-table">
                <thead>
                  <tr><th>Método</th><th>Endpoint</th><th>Promedio</th><th>Máximo</th><th>Peticiones</th><th>Errores</th></tr>
                </thead>
                <tbody>
                  <tr v-for="row in api.slowest_endpoints || []" :key="'slow' + row.method + row.endpoint">
                    <td><span class="itm-method">{{ row.method }}</span></td>
                    <td class="itm-mono">{{ row.endpoint }}</td>
                    <td :class="{ 'itm-warn': row.avg_ms > 800 }">{{ row.avg_ms }} ms</td>
                    <td :class="{ 'itm-down': row.max_ms > 5000 }">{{ row.max_ms }} ms</td>
                    <td>{{ row.count }}</td>
                    <td :class="{ 'itm-down': row.errors > 0 }">{{ row.errors }}</td>
                  </tr>
                  <tr v-if="!(api.slowest_endpoints || []).length">
                    <td colspan="6" class="itm-muted">Sin datos</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div>
              <h4 class="itm-squares-title">Más tiempo total consumido (lentos y/o muy usados)</h4>
              <table class="itm-table">
                <thead>
                  <tr><th>Método</th><th>Endpoint</th><th>Tiempo total</th><th>Promedio</th><th>Peticiones</th><th>Errores</th></tr>
                </thead>
                <tbody>
                  <tr v-for="row in api.heaviest_endpoints || []" :key="'heavy' + row.method + row.endpoint">
                    <td><span class="itm-method">{{ row.method }}</span></td>
                    <td class="itm-mono">{{ row.endpoint }}</td>
                    <td>{{ row.total_seconds }} s</td>
                    <td>{{ row.avg_ms }} ms</td>
                    <td>{{ row.count }}</td>
                    <td :class="{ 'itm-down': row.errors > 0 }">{{ row.errors }}</td>
                  </tr>
                  <tr v-if="!(api.heaviest_endpoints || []).length">
                    <td colspan="6" class="itm-muted">Sin datos</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="itm-card itm-card--full">
          <h3>Endpoints más lentos por bloque de 15 min (última hora)</h3>
          <div class="itm-slice-grid">
            <div v-for="slice in api.slowest_slices || []" :key="slice.ts" class="itm-slice">
              <h4 class="itm-squares-title">{{ sliceRange(slice.ts, slice.slice_minutes) }}</h4>
              <table class="itm-table">
                <thead>
                  <tr><th>Endpoint</th><th>Prom</th><th>Máx</th><th>#</th></tr>
                </thead>
                <tbody>
                  <tr v-for="row in slice.endpoints" :key="slice.ts + row.method + row.endpoint">
                    <td class="itm-mono" :title="row.method + ' ' + row.endpoint">{{ row.endpoint }}</td>
                    <td :class="{ 'itm-warn': row.avg_ms > 800 }">{{ row.avg_ms }}</td>
                    <td :class="{ 'itm-down': row.max_ms > 5000 }">{{ row.max_ms }}</td>
                    <td>{{ row.count }}</td>
                  </tr>
                  <tr v-if="!slice.endpoints.length">
                    <td colspan="4" class="itm-muted">Sin tráfico</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-if="!(api.slowest_slices || []).length" class="itm-muted">Sin datos todavía.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Insurer (AXA) API -->
    <section class="itm-section itm-rise" style="--rise: 4">
      <h2 class="itm-section__title">API Aseguradora (AXA)</h2>
      <div class="itm-grid itm-grid--api">
        <div class="itm-card">
          <div class="itm-device__head">
            <h3>Conexión</h3>
            <span class="itm-dot" :class="insurer.probe?.reachable ? 'itm-dot--ok' : 'itm-dot--down'"></span>
          </div>
          <dl class="itm-kv">
            <div><dt>Integración</dt>
              <dd :class="insurer.enabled ? 'itm-ok' : 'itm-warn'">
                {{ insurer.enabled == null ? '—' : (insurer.enabled ? 'Activa' : 'Deshabilitada') }}
              </dd></div>
            <div><dt>Host</dt><dd class="itm-mono">{{ insurer.host || '—' }}</dd></div>
            <div><dt>Estado</dt><dd :class="insurer.probe?.reachable ? 'itm-ok' : 'itm-down'">
              {{ insurer.probe?.reachable == null ? '—' : (insurer.probe.reachable ? 'Conectado' : 'Sin conexión') }}
            </dd></div>
            <div><dt>Latencia TCP</dt><dd>{{ insurer.probe?.latency_ms != null ? insurer.probe.latency_ms + ' ms' : '—' }}</dd></div>
            <div><dt>Intervalo de poll</dt><dd>{{ insurer.poll_interval_seconds != null ? insurer.poll_interval_seconds + ' s' : '—' }}</dd></div>
            <div><dt>Último poll</dt><dd>{{ formatDateTime(insurer.sync?.last_poll_at) }}</dd></div>
            <div><dt>Último éxito</dt><dd>{{ formatDateTime(insurer.sync?.last_success_at) }}</dd></div>
            <div><dt>Polls acumulados</dt><dd>{{ insurer.sync?.polls_total ?? '—' }}</dd></div>
            <div v-if="insurer.sync?.last_error"><dt>Último error</dt>
              <dd class="itm-down itm-small">{{ insurer.sync.last_error }}</dd></div>
          </dl>
        </div>
        <div class="itm-card">
          <h3>Última hora</h3>
          <dl class="itm-kv">
            <div><dt>Polls</dt><dd>{{ insurer.stats?.polls ?? '—' }}</dd></div>
            <div><dt>Por minuto</dt><dd>{{ insurer.stats?.polls_per_minute ?? '—' }}</dd></div>
            <div><dt>Fallidos</dt><dd :class="{ 'itm-down': insurer.stats?.failures > 0 }">{{ insurer.stats?.failures ?? '—' }}</dd></div>
            <div><dt>Éxito</dt><dd :class="insurer.stats?.success_percent != null && insurer.stats.success_percent < 99 ? 'itm-warn' : 'itm-ok'">
              {{ insurer.stats?.success_percent != null ? insurer.stats.success_percent + '%' : '—' }}
            </dd></div>
            <div><dt>Ciclo promedio</dt><dd>{{ insurer.stats?.avg_ms != null ? insurer.stats.avg_ms + ' ms' : '—' }}</dd></div>
            <div><dt>Ciclo máximo</dt><dd>{{ insurer.stats?.max_ms != null ? insurer.stats.max_ms + ' ms' : '—' }}</dd></div>
            <div><dt>Servicios en última lectura</dt><dd>{{ insurer.sync?.last_services_seen ?? '—' }}</dd></div>
          </dl>
          <h4 class="itm-squares-title">Salud por bloque de {{ apiSliceMinutes }} min ({{ historyHours }}h)</h4>
          <HealthSquares :slots="insurerSquares" show-legend />
        </div>
        <div class="itm-card itm-card--wide">
          <h3>Latencia y polls ({{ historyHours }}h)</h3>
          <v-chart :option="insurerHistoryOption" autoresize class="itm-chart itm-chart--tall" />
        </div>
      </div>
    </section>

    <!-- DB -->
    <section class="itm-section itm-rise" style="--rise: 5">
      <h2 class="itm-section__title">Base de datos (PostgreSQL)</h2>
      <div class="itm-grid itm-grid--db">
        <div class="itm-card">
          <h3>General</h3>
          <dl class="itm-kv">
            <div><dt>Tamaño</dt><dd>{{ db.size_mb != null ? formatSize(db.size_mb) : '—' }}</dd></div>
            <div><dt>Cache hit</dt>
              <dd :class="db.cache_hit_percent != null && db.cache_hit_percent < 95 ? 'itm-warn' : 'itm-ok'">
                {{ db.cache_hit_percent != null ? db.cache_hit_percent + '%' : '—' }}
              </dd></div>
            <div><dt>Consulta más larga</dt>
              <dd :class="{ 'itm-warn': db.longest_query_seconds > 30 }">
                {{ db.longest_query_seconds != null ? db.longest_query_seconds + ' s' : '—' }}
              </dd></div>
          </dl>
        </div>
        <div class="itm-card">
          <h3>Conexiones</h3>
          <dl class="itm-kv">
            <div v-for="(count, state) in db.connections || {}" :key="state">
              <dt class="itm-mono">{{ state }}</dt><dd>{{ count }}</dd>
            </div>
          </dl>
          <p v-if="!Object.keys(db.connections || {}).length" class="itm-muted">Sin datos</p>
        </div>
        <div class="itm-card">
          <h3>Tablas más grandes</h3>
          <dl class="itm-kv">
            <div v-for="table in db.top_tables || []" :key="table.table">
              <dt class="itm-mono">{{ table.table }}</dt><dd>{{ formatSize(table.size_mb) }}</dd>
            </div>
          </dl>
        </div>
        <div class="itm-card itm-card--wide">
          <h3>Conexiones y tamaño ({{ historyHours }}h)</h3>
          <v-chart :option="dbHistoryOption" autoresize class="itm-chart" />
        </div>
      </div>
    </section>

    <!-- Redis + Django workers -->
    <section class="itm-section itm-rise" style="--rise: 6">
      <h2 class="itm-section__title">Redis y procesos Django</h2>
      <div class="itm-grid itm-grid--api">
        <div class="itm-card">
          <div class="itm-device__head">
            <h3>Redis</h3>
            <span class="itm-dot" :class="redisStats.available ? 'itm-dot--ok' : 'itm-dot--down'"></span>
          </div>
          <dl class="itm-kv">
            <div><dt>Estado</dt><dd :class="redisStats.available ? 'itm-ok' : 'itm-down'">
              {{ redisStats.available == null ? '—' : (redisStats.available ? 'Conectado' : 'Sin conexión') }}
            </dd></div>
            <div><dt>Versión</dt><dd class="itm-mono">{{ redisStats.version || '—' }}</dd></div>
            <div><dt>Uptime</dt><dd class="itm-mono">{{ formatUptime(redisStats.uptime_seconds) }}</dd></div>
            <div><dt>Latencia (ping)</dt><dd>{{ redisStats.latency_ms != null ? redisStats.latency_ms + ' ms' : '—' }}</dd></div>
            <div><dt>Memoria usada</dt><dd>{{ redisStats.used_memory_mb != null ? formatSize(redisStats.used_memory_mb) : '—' }}</dd></div>
            <div><dt>Pico de memoria</dt><dd>{{ redisStats.used_memory_peak_mb != null ? formatSize(redisStats.used_memory_peak_mb) : '—' }}</dd></div>
            <div><dt>Clientes</dt><dd>{{ redisStats.connected_clients ?? '—' }}</dd></div>
            <div><dt>Ops/s</dt><dd>{{ redisStats.ops_per_sec ?? '—' }}</dd></div>
            <div><dt>Hit rate</dt><dd>{{ redisStats.hit_percent != null ? redisStats.hit_percent + '%' : '—' }}</dd></div>
            <div><dt>Claves</dt><dd>{{ redisStats.total_keys ?? '—' }}</dd></div>
            <div v-if="redisStats.evicted_keys"><dt>Claves expulsadas</dt>
              <dd class="itm-warn">{{ redisStats.evicted_keys }}</dd></div>
            <div v-if="redisStats.error"><dt>Error</dt>
              <dd class="itm-down itm-small">{{ redisStats.error }}</dd></div>
          </dl>
          <h4 class="itm-squares-title">Disponibilidad ({{ historyHours }}h)</h4>
          <HealthSquares :slots="redisSquares" />
        </div>
        <div class="itm-card itm-card--wide">
          <h3>Redis: memoria, clientes y tráfico ({{ historyHours }}h)</h3>
          <v-chart :option="redisHistoryOption" autoresize class="itm-chart itm-chart--tall" />
        </div>
        <div class="itm-card">
          <div class="itm-device__head">
            <h3>Workers</h3>
            <span class="itm-dot" :class="workers.available && !(workers.expected_missing || []).length ? 'itm-dot--ok' : 'itm-dot--down'"></span>
          </div>
          <dl class="itm-kv">
            <div><dt>Procesos</dt><dd>{{ workers.total_processes ?? '—' }}</dd></div>
            <div><dt>CPU total</dt><dd>{{ workers.total_cpu_percent != null ? workers.total_cpu_percent + '%' : '—' }}</dd></div>
            <div><dt>RAM total</dt><dd>{{ workers.total_memory_mb != null ? formatSize(workers.total_memory_mb) : '—' }}</dd></div>
            <div v-if="(workers.expected_missing || []).length"><dt>Faltantes</dt>
              <dd class="itm-down itm-small">{{ workers.expected_missing.join(', ') }}</dd></div>
            <div v-if="workers.error"><dt>Error</dt>
              <dd class="itm-down itm-small">{{ workers.error }}</dd></div>
          </dl>
          <h4 class="itm-squares-title">Disponibilidad ({{ historyHours }}h)</h4>
          <HealthSquares :slots="workersSquares" />
        </div>
        <div class="itm-card itm-card--wide">
          <h3>Procesos activos (detalle por worker)</h3>
          <table class="itm-table">
            <thead>
              <tr><th>Proceso</th><th>PID</th><th>Rol</th><th>CPU</th><th>RAM</th><th>Hilos</th><th>Uptime</th></tr>
            </thead>
            <tbody>
              <template v-for="group in workers.groups || []" :key="group.key">
                <tr v-for="(proc, idx) in group.procs || []" :key="group.key + '-' + proc.pid">
                  <td class="itm-mono">{{ idx === 0 ? group.label : '' }}</td>
                  <td class="itm-mono">{{ proc.pid }}</td>
                  <td>{{ proc.role || '—' }}</td>
                  <td :class="{ 'itm-warn': proc.cpu_percent > 80 }">{{ proc.cpu_percent }}%</td>
                  <td>{{ formatSize(proc.memory_mb) }}</td>
                  <td>{{ proc.threads ?? '—' }}</td>
                  <td class="itm-mono">{{ formatUptime(proc.uptime_seconds) }}</td>
                </tr>
                <tr v-if="!(group.procs || []).length">
                  <td class="itm-mono">{{ group.label }}</td>
                  <td class="itm-mono">—</td>
                  <td>—</td>
                  <td>{{ group.cpu_percent }}%</td>
                  <td>{{ formatSize(group.memory_mb) }}</td>
                  <td>—</td>
                  <td class="itm-mono">{{ formatUptime(group.uptime_seconds) }}</td>
                </tr>
              </template>
              <tr v-for="label in workers.expected_missing || []" :key="'missing-' + label">
                <td class="itm-mono itm-down">{{ label }}</td>
                <td colspan="6" class="itm-down">Sin proceso activo</td>
              </tr>
              <tr v-if="!(workers.groups || []).length && !(workers.expected_missing || []).length">
                <td colspan="7" class="itm-muted">Sin datos</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="itm-card itm-card--wide">
          <h3>Workers: CPU, RAM y procesos ({{ historyHours }}h)</h3>
          <v-chart :option="workersHistoryOption" autoresize class="itm-chart itm-chart--tall" />
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import axios from 'axios';
import HealthSquares from '@/components/HealthSquares.vue';

const apiBase = process.env.API_DIRECTORY || '';
const authHeaders = () => ({ Authorization: 'Token ' + localStorage.getItem('authToken') });

const OVERVIEW_POLL_MS = 20000;
const HISTORY_POLL_MS = 5 * 60 * 1000;
const LIVE_POINTS_MAX = 90; // ~3 min at 2s pushes

const loading = ref(false);
const error = ref(null);
const lastUpdated = ref(null);
const secondsSinceUpdate = ref(0);

const vm = ref({});
const devices = ref([]);
const db = ref({});
const api = ref({});
const insurer = ref({});
const redisStats = ref({});
const workers = ref({});
const clocks = ref({});
const sampleIntervalSeconds = ref(300);
const historyHours = ref(24);
const apiSliceMinutes = ref(15);
const history = ref({ vm: [], devices: {}, db: [], api: [], insurer: [], redis: [], workers: [] });

// Live series pushed over the websocket
const live = reactive({ ts: [], cpu: [], mem: [] });
const wsStatus = ref('closed');

// Clock offsets: remote wall time vs local, ticked locally every second.
const clockOffsets = reactive({ monterrey: null, server: null, db: null });
const nowTick = ref(Date.now());

// Uptime ticks locally between refreshes so the counter is always moving.
const vmReceivedAt = ref(null);

let ws = null;
let wsRetries = 0;
let wsClosedByUs = false;
let overviewTimer = null;
let historyTimer = null;
let clockTimer = null;

// ---------------------------------------------------------------------------
// Theme

const isDark = ref(true);
try {
  const saved = localStorage.getItem('itMonitorTheme');
  if (saved) isDark.value = saved === 'dark';
} catch (e) { /* ignore */ }

function toggleTheme() {
  isDark.value = !isDark.value;
  try { localStorage.setItem('itMonitorTheme', isDark.value ? 'dark' : 'light'); } catch (e) { /* ignore */ }
}

const pal = computed(() => (isDark.value
  ? {
    text: '#e6edf3', muted: '#7d8590', grid: '#21262d',
    cpu: '#58a6ff', mem: '#d2a8ff', ok: '#39d353', warn: '#d29922', down: '#f85149',
    barFill: 'rgba(57, 211, 83, 0.35)',
    tooltipBg: 'rgba(13, 18, 30, 0.96)', tooltipText: '#e6edf3',
  }
  : {
    text: '#0f172a', muted: '#64748b', grid: '#e2e8f0',
    cpu: '#2563eb', mem: '#7c3aed', ok: '#16a34a', warn: '#d97706', down: '#dc2626',
    barFill: 'rgba(22, 163, 74, 0.30)',
    tooltipBg: 'rgba(255, 255, 255, 0.98)', tooltipText: '#0f172a',
  }));

// ---------------------------------------------------------------------------
// Fetching

async function fetchOverview() {
  try {
    const { data } = await axios.get(`${apiBase}/monitoring/overview`, { headers: authHeaders() });
    setVm(data.vm || {});
    devices.value = data.devices || [];
    db.value = data.db || {};
    api.value = data.api || {};
    insurer.value = data.insurer || {};
    redisStats.value = data.redis || {};
    workers.value = data.workers || {};
    clocks.value = data.clocks || {};
    sampleIntervalSeconds.value = data.sample_interval_seconds || 300;
    for (const key of ['monterrey', 'server', 'db']) {
      clockOffsets[key] = wallClockOffset(data.clocks?.[key]);
    }
    lastUpdated.value = Date.now();
    error.value = null;
  } catch (e) {
    error.value = e?.response?.status === 403
      ? 'No tienes permisos para ver el monitor de infraestructura.'
      : 'No se pudo obtener el estado de la infraestructura.';
  }
}

async function fetchHistory() {
  try {
    const { data } = await axios.get(`${apiBase}/monitoring/history`, { headers: authHeaders() });
    history.value = data;
    historyHours.value = data.hours || 24;
    apiSliceMinutes.value = data.api_slice_minutes || 15;
    if (data.sample_interval_seconds) sampleIntervalSeconds.value = data.sample_interval_seconds;
  } catch (e) {
    /* overview already surfaces errors; history is best-effort */
  }
}

async function fetchAll() {
  loading.value = true;
  await Promise.all([fetchOverview(), fetchHistory()]);
  loading.value = false;
}

function setVm(stats) {
  vm.value = stats;
  if (stats.available) vmReceivedAt.value = Date.now();
}

// Each clock ticks the remote *wall* time: we parse the ISO string's local
// part as if it were UTC and render with timeZone UTC, so "11:39 in Monterrey"
// stays "11:39" regardless of the browser's timezone.
function wallClockOffset(iso) {
  if (!iso) return null;
  return new Date(`${iso.slice(0, 19)}Z`).getTime() - Date.now();
}

// ---------------------------------------------------------------------------
// Live VM websocket

function connectWS() {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  ws = new WebSocket(`${proto}://${window.location.host}/ws/monitoring/vm/`);
  ws.onopen = () => {
    wsStatus.value = 'open';
    wsRetries = 0;
  };
  ws.onmessage = (evt) => {
    try {
      const msg = JSON.parse(evt.data);
      if (msg.type !== 'vm_stats' || !msg.vm?.available) return;
      setVm(msg.vm);
      live.ts.push(new Date().toLocaleTimeString('es-MX', { hour12: false }));
      live.cpu.push(msg.vm.cpu_percent);
      live.mem.push(msg.vm.memory_percent);
      if (live.ts.length > LIVE_POINTS_MAX) {
        live.ts.shift();
        live.cpu.shift();
        live.mem.shift();
      }
    } catch (e) { /* ignore malformed frames */ }
  };
  ws.onclose = () => {
    if (wsClosedByUs) return;
    wsStatus.value = 'retry';
    const backoff = Math.min(1000 * 2 ** (wsRetries += 1), 15000);
    setTimeout(connectWS, backoff);
  };
  ws.onerror = () => {
    try { ws.close(); } catch (e) { /* already closed */ }
  };
}

// ---------------------------------------------------------------------------
// Derived state

const statusTiles = computed(() => {
  const tiles = [];

  let vmStatus = 'empty';
  if (vm.value.available) {
    const diskMax = Math.max(...(vm.value.disks || []).map((d) => d.percent), 0);
    vmStatus = vm.value.cpu_percent > 85 || vm.value.memory_percent > 90 || diskMax > 85 ? 'warn' : 'ok';
  } else if (vm.value.available === false) {
    vmStatus = 'down';
  }
  tiles.push({
    key: 'vm', name: 'Servidor', status: vmStatus,
    value: vm.value.available ? `${vm.value.cpu_percent}% CPU` : '—',
    detail: vm.value.available ? `${vm.value.memory_percent}% RAM` : 'sin datos',
  });

  for (const device of devices.value) {
    tiles.push({
      key: device.key,
      name: device.label,
      status: device.reachable ? (device.sync?.last_error ? 'warn' : 'ok') : 'down',
      value: device.reachable ? `${device.latency_ms} ms` : 'Caído',
      detail: device.ip,
    });
  }

  tiles.push({
    key: 'db', name: 'Base de datos',
    status: db.value.available ? 'ok' : (db.value.available === false ? 'down' : 'empty'),
    value: db.value.size_mb != null ? formatSize(db.value.size_mb) : '—',
    detail: db.value.connections
      ? `${Object.values(db.value.connections).reduce((a, b) => a + b, 0)} conexiones` : 'sin datos',
  });

  const requests = api.value.requests || 0;
  const errRate = requests ? (api.value.errors || 0) / requests : 0;
  tiles.push({
    key: 'api', name: 'API',
    status: requests ? (errRate > 0.05 ? 'warn' : 'ok') : 'empty',
    value: api.value.avg_ms != null ? `${api.value.avg_ms} ms` : '—',
    detail: requests ? `${requests} req/h` : 'sin datos',
  });

  tiles.push({
    key: 'insurer', name: 'API AXA',
    status: insurerStatus.value,
    value: insurer.value?.sync?.last_latency_ms != null ? `${insurer.value.sync.last_latency_ms} ms` : '—',
    detail: insurer.value?.enabled === false
      ? 'integración deshabilitada'
      : (insurer.value?.stats?.polls ? `${insurer.value.stats.polls} polls/h` : 'sin polls'),
  });

  const rds = redisStats.value || {};
  tiles.push({
    key: 'redis', name: 'Redis',
    status: rds.available ? 'ok' : (rds.available === false ? 'down' : 'empty'),
    value: rds.used_memory_mb != null ? formatSize(rds.used_memory_mb) : '—',
    detail: rds.available ? `${rds.connected_clients ?? 0} clientes` : 'sin datos',
  });

  const wrk = workers.value || {};
  const missing = wrk.expected_missing || [];
  tiles.push({
    key: 'workers', name: 'Workers',
    status: wrk.available ? (missing.length ? 'down' : 'ok') : 'empty',
    value: wrk.total_processes != null ? `${wrk.total_processes} proc` : '—',
    detail: missing.length ? `falta ${missing.join(', ')}` : (wrk.available ? `${wrk.total_cpu_percent ?? 0}% CPU` : 'sin datos'),
  });

  return tiles;
});

// Insurer (AXA) health: staleness of the polling loop beats the raw TCP probe.
const insurerStatus = computed(() => {
  const ins = insurer.value || {};
  if (!Object.keys(ins).length) return 'empty';
  if (ins.enabled === false) return 'empty';
  const sync = ins.sync || {};
  if (!sync.last_poll_at) return ins.probe?.reachable === false ? 'down' : 'empty';
  const staleMs = Math.max((ins.poll_interval_seconds || 5) * 3, 60) * 1000;
  const lastSuccess = sync.last_success_at ? new Date(sync.last_success_at).getTime() : 0;
  if (nowTick.value - lastSuccess > staleMs) return 'down';
  if (ins.probe?.reachable === false) return 'warn';
  const stats = ins.stats || {};
  if ((stats.failures || 0) > 0) return 'warn';
  return 'ok';
});

const clockCards = computed(() => {
  const cards = [];
  const defs = [
    { key: 'monterrey', label: 'Monterrey' },
    { key: 'server', label: 'Servidor' },
    { key: 'db', label: 'Base de datos' },
  ];
  for (const def of defs) {
    if (clockOffsets[def.key] == null) {
      cards.push({ label: def.label, time: '--:--:--', date: '' });
      continue;
    }
    const dt = new Date(nowTick.value + clockOffsets[def.key]);
    cards.push({
      label: def.label,
      time: dt.toLocaleTimeString('es-MX', { hour12: false, timeZone: 'UTC' }),
      date: dt.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', timeZone: 'UTC' }),
    });
  }
  return cards;
});

const uptimeText = computed(() => {
  let seconds = vm.value.uptime_seconds;
  if (seconds == null) return '—';
  if (vmReceivedAt.value) {
    seconds += Math.floor((nowTick.value - vmReceivedAt.value) / 1000);
  }
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return days > 0 ? `${days}d ${hours}h ${mins}m ${secs}s` : `${hours}h ${mins}m ${secs}s`;
});

const loadAvgText = computed(() => {
  const load = vm.value.load_avg;
  if (!load || load[0] == null) return '—';
  return load.map((v) => v.toFixed(2)).join(' / ');
});

// GitHub-style squares -------------------------------------------------------

function buildTimeSlots(intervalMs) {
  const end = Date.now();
  const start = end - historyHours.value * 3600 * 1000;
  const slots = [];
  for (let ts = start; ts < end; ts += intervalMs) slots.push(ts);
  return slots;
}

const deviceSquares = computed(() => {
  const intervalMs = sampleIntervalSeconds.value * 1000;
  const result = {};
  for (const [key, series] of Object.entries(history.value.devices || {})) {
    const byBucket = new Map();
    for (const point of series) {
      byBucket.set(Math.floor(new Date(point.ts).getTime() / intervalMs), point);
    }
    result[key] = buildTimeSlots(intervalMs).map((ts) => {
      const point = byBucket.get(Math.floor(ts / intervalMs));
      const when = new Date(ts).toLocaleString('es-MX', { hour12: false });
      if (!point) return { status: 'empty', label: `${when} — sin datos` };
      if (!point.reachable) return { status: 'down', label: `${when} — sin conexión` };
      const slow = point.latency_ms != null && point.latency_ms > 500;
      return {
        status: slow ? 'slow' : 'ok',
        label: `${when} — ${point.latency_ms} ms`,
      };
    });
  }
  return result;
});

const apiSquares = computed(() => {
  const intervalMs = apiSliceMinutes.value * 60 * 1000;
  const byBucket = new Map();
  for (const point of history.value.api || []) {
    byBucket.set(Math.floor(new Date(point.ts).getTime() / intervalMs), point);
  }
  return buildTimeSlots(intervalMs).map((ts) => {
    const point = byBucket.get(Math.floor(ts / intervalMs));
    const when = new Date(ts).toLocaleString('es-MX', { hour12: false });
    if (!point || !point.count) return { status: 'empty', label: `${when} — sin tráfico` };
    const errRate = point.errors / point.count;
    let status = 'ok';
    if (errRate > 0.2) status = 'down';
    else if (errRate > 0.02) status = 'warn';
    else if (point.avg_ms > 800) status = 'slow';
    return {
      status,
      label: `${when} — ${point.count} req, ${point.avg_ms} ms prom, ${point.errors} errores`,
    };
  });
});

// Sampler-driven squares (one cell per sample interval, like devices).
function sampleSquares(series, describe) {
  const intervalMs = sampleIntervalSeconds.value * 1000;
  const byBucket = new Map();
  for (const point of series || []) {
    byBucket.set(Math.floor(new Date(point.ts).getTime() / intervalMs), point);
  }
  return buildTimeSlots(intervalMs).map((ts) => {
    const point = byBucket.get(Math.floor(ts / intervalMs));
    const when = new Date(ts).toLocaleString('es-MX', { hour12: false });
    if (!point) return { status: 'empty', label: `${when} — sin datos` };
    const { status, text } = describe(point);
    return { status, label: `${when} — ${text}` };
  });
}

const redisSquares = computed(() => sampleSquares(history.value.redis, (point) => {
  if (!point.available) return { status: 'down', text: 'Redis caído' };
  const slow = point.latency_ms != null && point.latency_ms > 50;
  return {
    status: slow ? 'slow' : 'ok',
    text: `${point.latency_ms} ms, ${formatSize(point.used_memory_mb)}, ${point.connected_clients} clientes`,
  };
}));

const workersSquares = computed(() => sampleSquares(history.value.workers, (point) => {
  if (!point.available) return { status: 'down', text: 'sin lectura de procesos' };
  if ((point.missing || []).length) return { status: 'down', text: `falta ${point.missing.join(', ')}` };
  const busy = point.cpu_percent != null && point.cpu_percent > 150;
  return {
    status: busy ? 'warn' : 'ok',
    text: `${point.processes} procesos, ${point.cpu_percent}% CPU, ${formatSize(point.memory_mb)}`,
  };
}));

const insurerSquares = computed(() => {
  const intervalMs = apiSliceMinutes.value * 60 * 1000;
  const byBucket = new Map();
  for (const point of history.value.insurer || []) {
    byBucket.set(Math.floor(new Date(point.ts).getTime() / intervalMs), point);
  }
  return buildTimeSlots(intervalMs).map((ts) => {
    const point = byBucket.get(Math.floor(ts / intervalMs));
    const when = new Date(ts).toLocaleString('es-MX', { hour12: false });
    if (!point || !point.polls) return { status: 'empty', label: `${when} — sin polls` };
    const failRate = point.failures / point.polls;
    let status = 'ok';
    if (failRate > 0.5) status = 'down';
    else if (failRate > 0.05) status = 'warn';
    else if (point.avg_ms != null && point.avg_ms > 5000) status = 'slow';
    return {
      status,
      label: `${when} — ${point.polls} polls, ${point.avg_ms} ms prom, ${point.failures} fallidos`,
    };
  });
});

// ECharts options -------------------------------------------------------------

function gaugeOption(value, color) {
  return {
    series: [{
      type: 'gauge',
      startAngle: 210, endAngle: -30, min: 0, max: 100,
      progress: { show: true, width: 12, itemStyle: { color } },
      axisLine: { lineStyle: { width: 12, color: [[1, pal.value.grid]] } },
      axisTick: { show: false }, splitLine: { show: false }, axisLabel: { show: false },
      pointer: { show: false },
      detail: {
        valueAnimation: true, offsetCenter: [0, 0],
        formatter: '{value}%', color: pal.value.text, fontSize: 26, fontWeight: 600,
      },
      data: [{ value: value ?? 0 }],
    }],
  };
}

const cpuGaugeOption = computed(() => gaugeOption(vm.value.cpu_percent, pal.value.cpu));
const memGaugeOption = computed(() => gaugeOption(vm.value.memory_percent, pal.value.mem));

const baseAxis = computed(() => ({
  axisLabel: { color: pal.value.muted, fontSize: 10 },
  axisLine: { lineStyle: { color: pal.value.grid } },
  splitLine: { lineStyle: { color: pal.value.grid } },
}));

const baseTooltip = computed(() => ({
  trigger: 'axis',
  backgroundColor: pal.value.tooltipBg,
  borderColor: pal.value.grid,
  textStyle: { color: pal.value.tooltipText },
}));

const liveChartOption = computed(() => ({
  backgroundColor: 'transparent',
  animationDurationUpdate: 300,
  tooltip: baseTooltip.value,
  legend: { data: ['CPU', 'Memoria'], textStyle: { color: pal.value.muted }, top: 0 },
  grid: { left: 40, right: 12, top: 28, bottom: 22 },
  xAxis: { type: 'category', data: [...live.ts], ...baseAxis.value },
  yAxis: { type: 'value', min: 0, max: 100, ...baseAxis.value },
  series: [
    {
      name: 'CPU', type: 'line', data: [...live.cpu], showSymbol: false, smooth: true,
      lineStyle: { color: pal.value.cpu }, itemStyle: { color: pal.value.cpu },
      areaStyle: { color: pal.value.cpu, opacity: 0.08 },
    },
    {
      name: 'Memoria', type: 'line', data: [...live.mem], showSymbol: false, smooth: true,
      lineStyle: { color: pal.value.mem }, itemStyle: { color: pal.value.mem },
      areaStyle: { color: pal.value.mem, opacity: 0.08 },
    },
  ],
}));

function formatShortTime(iso) {
  return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
}

const vmHistoryOption = computed(() => {
  const series = history.value.vm || [];
  return {
    backgroundColor: 'transparent',
    tooltip: baseTooltip.value,
    legend: { data: ['CPU', 'Memoria', 'Disco'], textStyle: { color: pal.value.muted }, top: 0 },
    grid: { left: 40, right: 12, top: 28, bottom: 22 },
    xAxis: { type: 'category', data: series.map((p) => formatShortTime(p.ts)), ...baseAxis.value },
    yAxis: { type: 'value', min: 0, max: 100, ...baseAxis.value },
    series: [
      { name: 'CPU', type: 'line', data: series.map((p) => p.cpu), showSymbol: false, lineStyle: { color: pal.value.cpu }, itemStyle: { color: pal.value.cpu } },
      { name: 'Memoria', type: 'line', data: series.map((p) => p.memory), showSymbol: false, lineStyle: { color: pal.value.mem }, itemStyle: { color: pal.value.mem } },
      { name: 'Disco', type: 'line', data: series.map((p) => p.disk_max), showSymbol: false, lineStyle: { color: pal.value.warn }, itemStyle: { color: pal.value.warn } },
    ],
  };
});

const apiHistoryOption = computed(() => {
  const series = history.value.api || [];
  return {
    backgroundColor: 'transparent',
    tooltip: baseTooltip.value,
    legend: { data: ['Promedio ms', 'Máximo ms', 'Peticiones'], textStyle: { color: pal.value.muted }, top: 0 },
    grid: { left: 46, right: 46, top: 28, bottom: 22 },
    xAxis: { type: 'category', data: series.map((p) => formatShortTime(p.ts)), ...baseAxis.value },
    yAxis: [
      { type: 'value', name: 'ms', nameTextStyle: { color: pal.value.muted }, ...baseAxis.value },
      { type: 'value', name: 'req', nameTextStyle: { color: pal.value.muted }, ...baseAxis.value, splitLine: { show: false } },
    ],
    series: [
      { name: 'Promedio ms', type: 'line', data: series.map((p) => p.avg_ms), showSymbol: false, smooth: true, lineStyle: { color: pal.value.cpu }, itemStyle: { color: pal.value.cpu } },
      { name: 'Máximo ms', type: 'line', data: series.map((p) => p.max_ms), showSymbol: false, lineStyle: { color: pal.value.down, type: 'dashed' }, itemStyle: { color: pal.value.down } },
      { name: 'Peticiones', type: 'bar', yAxisIndex: 1, data: series.map((p) => p.count), itemStyle: { color: pal.value.barFill } },
    ],
  };
});

const insurerHistoryOption = computed(() => {
  const series = history.value.insurer || [];
  return {
    backgroundColor: 'transparent',
    tooltip: baseTooltip.value,
    legend: { data: ['Promedio ms', 'Máximo ms', 'Polls', 'Fallidos'], textStyle: { color: pal.value.muted }, top: 0 },
    grid: { left: 46, right: 46, top: 28, bottom: 22 },
    xAxis: { type: 'category', data: series.map((p) => formatShortTime(p.ts)), ...baseAxis.value },
    yAxis: [
      { type: 'value', name: 'ms', nameTextStyle: { color: pal.value.muted }, ...baseAxis.value },
      { type: 'value', name: 'polls', nameTextStyle: { color: pal.value.muted }, ...baseAxis.value, splitLine: { show: false } },
    ],
    series: [
      { name: 'Promedio ms', type: 'line', data: series.map((p) => p.avg_ms), showSymbol: false, smooth: true, lineStyle: { color: pal.value.cpu }, itemStyle: { color: pal.value.cpu } },
      { name: 'Máximo ms', type: 'line', data: series.map((p) => p.max_ms), showSymbol: false, lineStyle: { color: pal.value.warn, type: 'dashed' }, itemStyle: { color: pal.value.warn } },
      { name: 'Polls', type: 'bar', yAxisIndex: 1, data: series.map((p) => p.polls), itemStyle: { color: pal.value.barFill } },
      { name: 'Fallidos', type: 'bar', yAxisIndex: 1, stack: 'fails', data: series.map((p) => p.failures), itemStyle: { color: pal.value.down } },
    ],
  };
});

const dbHistoryOption = computed(() => {
  const series = history.value.db || [];
  return {
    backgroundColor: 'transparent',
    tooltip: baseTooltip.value,
    legend: { data: ['Conexiones', 'Tamaño MB'], textStyle: { color: pal.value.muted }, top: 0 },
    grid: { left: 46, right: 56, top: 28, bottom: 22 },
    xAxis: { type: 'category', data: series.map((p) => formatShortTime(p.ts)), ...baseAxis.value },
    yAxis: [
      { type: 'value', name: 'conex', nameTextStyle: { color: pal.value.muted }, ...baseAxis.value },
      { type: 'value', name: 'MB', nameTextStyle: { color: pal.value.muted }, ...baseAxis.value, splitLine: { show: false } },
    ],
    series: [
      { name: 'Conexiones', type: 'line', data: series.map((p) => p.connections), showSymbol: false, lineStyle: { color: pal.value.mem }, itemStyle: { color: pal.value.mem } },
      { name: 'Tamaño MB', type: 'line', yAxisIndex: 1, data: series.map((p) => p.size_mb), showSymbol: false, lineStyle: { color: pal.value.ok }, itemStyle: { color: pal.value.ok } },
    ],
  };
});

const redisHistoryOption = computed(() => {
  const series = history.value.redis || [];
  return {
    backgroundColor: 'transparent',
    tooltip: baseTooltip.value,
    legend: { data: ['Memoria MB', 'Clientes', 'Ops/s'], textStyle: { color: pal.value.muted }, top: 0 },
    grid: { left: 46, right: 46, top: 28, bottom: 22 },
    xAxis: { type: 'category', data: series.map((p) => formatShortTime(p.ts)), ...baseAxis.value },
    yAxis: [
      { type: 'value', name: 'MB', nameTextStyle: { color: pal.value.muted }, ...baseAxis.value },
      { type: 'value', name: 'conex/ops', nameTextStyle: { color: pal.value.muted }, ...baseAxis.value, splitLine: { show: false } },
    ],
    series: [
      { name: 'Memoria MB', type: 'line', data: series.map((p) => p.used_memory_mb), showSymbol: false, smooth: true, lineStyle: { color: pal.value.mem }, itemStyle: { color: pal.value.mem } },
      { name: 'Clientes', type: 'line', yAxisIndex: 1, data: series.map((p) => p.connected_clients), showSymbol: false, lineStyle: { color: pal.value.cpu }, itemStyle: { color: pal.value.cpu } },
      { name: 'Ops/s', type: 'bar', yAxisIndex: 1, data: series.map((p) => p.ops_per_sec), itemStyle: { color: pal.value.barFill } },
    ],
  };
});

const workersHistoryOption = computed(() => {
  const series = history.value.workers || [];
  return {
    backgroundColor: 'transparent',
    tooltip: baseTooltip.value,
    legend: { data: ['CPU %', 'RAM MB', 'Procesos'], textStyle: { color: pal.value.muted }, top: 0 },
    grid: { left: 46, right: 46, top: 28, bottom: 22 },
    xAxis: { type: 'category', data: series.map((p) => formatShortTime(p.ts)), ...baseAxis.value },
    yAxis: [
      { type: 'value', name: '% / MB', nameTextStyle: { color: pal.value.muted }, ...baseAxis.value },
      { type: 'value', name: 'proc', nameTextStyle: { color: pal.value.muted }, ...baseAxis.value, splitLine: { show: false } },
    ],
    series: [
      { name: 'CPU %', type: 'line', data: series.map((p) => p.cpu_percent), showSymbol: false, smooth: true, lineStyle: { color: pal.value.cpu }, itemStyle: { color: pal.value.cpu } },
      { name: 'RAM MB', type: 'line', data: series.map((p) => p.memory_mb), showSymbol: false, lineStyle: { color: pal.value.mem }, itemStyle: { color: pal.value.mem } },
      { name: 'Procesos', type: 'bar', yAxisIndex: 1, data: series.map((p) => p.processes), itemStyle: { color: pal.value.barFill } },
    ],
  };
});

// Helpers ---------------------------------------------------------------------

function barClass(percent) {
  if (percent > 85) return 'itm-bar__fill--down';
  if (percent > 70) return 'itm-bar__fill--warn';
  return '';
}

function formatSize(mb) {
  if (mb == null) return '—';
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
}

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-MX', { hour12: false });
}

function sliceRange(ts, minutes) {
  const start = new Date(ts);
  const end = new Date(start.getTime() + (minutes || 15) * 60000);
  const fmt = (d) => d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${fmt(start)} – ${fmt(end)}`;
}

function formatUptime(seconds) {
  if (seconds == null) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return days > 0 ? `${days}d ${hours}h ${mins}m` : `${hours}h ${mins}m`;
}

// Lifecycle --------------------------------------------------------------------

onMounted(() => {
  fetchAll();
  connectWS();
  overviewTimer = setInterval(fetchOverview, OVERVIEW_POLL_MS);
  historyTimer = setInterval(fetchHistory, HISTORY_POLL_MS);
  clockTimer = setInterval(() => {
    nowTick.value = Date.now();
    if (lastUpdated.value) {
      secondsSinceUpdate.value = Math.round((Date.now() - lastUpdated.value) / 1000);
    }
  }, 1000);
});

onBeforeUnmount(() => {
  clearInterval(overviewTimer);
  clearInterval(historyTimer);
  clearInterval(clockTimer);
  wsClosedByUs = true;
  try { if (ws) ws.close(); } catch (e) { /* ignore */ }
});
</script>

<style scoped>
/* ───────────────────────── Themes ───────────────────────── */
.itm {
  min-height: 100vh;
  padding: 18px 22px 40px;
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
  position: relative;
  transition: background-color 0.35s ease, color 0.35s ease;
  background: var(--bg);
  color: var(--text);
}

.itm--dark {
  color-scheme: dark;
  --bg: #0a0e17;
  --card: #111726;
  --card-hover: #141b2e;
  --border: #1c2333;
  --border-hover: #2f3b55;
  --text: #e6edf3;
  --muted: #7d8590;
  --ok: #39d353;
  --warn: #d29922;
  --down: #f85149;
  --accent: #58a6ff;
  --track: #1c2333;
  --shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  --logo-filter: brightness(0) invert(1);
  --ambient-a: rgba(37, 62, 128, 0.16);
  --ambient-b: rgba(57, 211, 83, 0.05);
  --hs-ok: #39d353;
  --hs-slow: #006d32;
  --hs-warn: #d29922;
  --hs-down: #f85149;
  --hs-empty: #1c2333;
  --hs-legend: #7d8590;
}

.itm--light {
  color-scheme: light;
  --bg: #f4f6fa;
  --card: #ffffff;
  --card-hover: #ffffff;
  --border: #e2e8f0;
  --border-hover: #94a3b8;
  --text: #0f172a;
  --muted: #64748b;
  --ok: #16a34a;
  --warn: #d97706;
  --down: #dc2626;
  --accent: #2563eb;
  --track: #e2e8f0;
  --shadow: 0 8px 24px rgba(15, 23, 42, 0.10);
  --logo-filter: none;
  --ambient-a: rgba(37, 99, 235, 0.06);
  --ambient-b: rgba(22, 163, 74, 0.04);
  --hs-ok: #16a34a;
  --hs-slow: #86efac;
  --hs-warn: #f59e0b;
  --hs-down: #dc2626;
  --hs-empty: #e2e8f0;
  --hs-legend: #64748b;
}

.itm-ambient {
  position: fixed;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(700px 420px at 12% -8%, var(--ambient-a), transparent 60%),
    radial-gradient(800px 500px at 95% 8%, var(--ambient-b), transparent 60%);
  transition: opacity 0.35s ease;
}

.itm > *:not(.itm-ambient) {
  position: relative;
}

/* ───────────────────────── Header ───────────────────────── */
.itm-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 18px;
}
.itm-header__left {
  display: flex;
  align-items: center;
  gap: 16px;
}
.itm-logo {
  height: 44px;
  cursor: pointer;
  filter: var(--logo-filter);
  transition: transform 0.2s ease, filter 0.35s ease;
}
.itm-logo:hover {
  transform: scale(1.05);
}
.itm-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
}
.itm-sub {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--muted);
  display: flex;
  align-items: center;
  gap: 6px;
}
.itm-live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--down);
  display: inline-block;
}
.itm-live-dot--on {
  background: var(--ok);
  animation: itm-pulse 2s ease-in-out infinite;
}
@keyframes itm-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(57, 211, 83, 0.55); }
  50% { box-shadow: 0 0 0 6px rgba(57, 211, 83, 0); }
}
.itm-header__right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.itm-clock {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 6px 14px;
  min-width: 108px;
  transition: background-color 0.35s ease, border-color 0.35s ease;
}
.itm-clock__label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--muted);
}
.itm-clock__time {
  font-size: 20px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.itm-clock__date {
  font-size: 10px;
  color: var(--muted);
}
.itm-btn {
  background: var(--card);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 13px;
  transition: background-color 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
}
.itm-btn:hover {
  border-color: var(--border-hover);
  transform: translateY(-1px);
}
.itm-btn:disabled { opacity: 0.5; cursor: default; }
.itm-btn--icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 0;
}
.itm-btn--icon svg { width: 17px; height: 17px; }
.itm-btn--spinning svg {
  animation: itm-spin 0.9s linear infinite;
}
@keyframes itm-spin {
  to { transform: rotate(360deg); }
}

.itm-error {
  background: color-mix(in srgb, var(--down) 12%, transparent);
  border: 1px solid var(--down);
  color: var(--down);
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 14px;
  font-size: 13px;
}

/* ───────────────────────── Entrance animation ───────────────────────── */
.itm-rise {
  animation: itm-rise 0.5s ease-out backwards;
  animation-delay: calc(var(--rise, 0) * 90ms);
}
@keyframes itm-rise {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ───────────────────────── Status tiles ───────────────────────── */
.itm-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: 22px;
}
.itm-tile {
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  border: 1px solid var(--border);
  background: var(--card);
  border-left-width: 4px;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background-color 0.35s ease, border-color 0.35s ease;
}
.itm-tile:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow);
}
.itm-tile--ok { border-left-color: var(--ok); }
.itm-tile--warn { border-left-color: var(--warn); }
.itm-tile--down {
  border-left-color: var(--down);
  background: color-mix(in srgb, var(--down) 7%, var(--card));
  animation: itm-alert 2.4s ease-in-out infinite;
}
@keyframes itm-alert {
  0%, 100% { box-shadow: 0 0 0 0 rgba(248, 81, 73, 0.25); }
  50% { box-shadow: 0 0 14px 1px rgba(248, 81, 73, 0.18); }
}
.itm-tile--empty { border-left-color: var(--muted); }
.itm-tile__name {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--muted);
}
.itm-tile__value {
  font-size: 20px;
  font-weight: 700;
}
.itm-tile__detail {
  font-size: 11px;
  color: var(--muted);
}

/* ───────────────────────── Sections and cards ───────────────────────── */
.itm-section { margin-bottom: 26px; }
.itm-section__title {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--muted);
  margin: 0 0 10px;
}
.itm-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(12, 1fr);
}
.itm-card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 14px 16px;
  grid-column: span 3;
  min-width: 0;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.2s ease, background-color 0.35s ease;
}
.itm-card:hover {
  transform: translateY(-2px);
  border-color: var(--border-hover);
  box-shadow: var(--shadow);
  background: var(--card-hover);
}
.itm-card--wide { grid-column: span 6; }
.itm-card--full { grid-column: span 12; }
.itm-grid--devices .itm-card { grid-column: span 4; }
.itm-card h3 {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
}
.itm-gauge { height: 150px; }
.itm-chart { height: 180px; }
.itm-chart--tall { height: 260px; }

/* ───────────────────────── Key-values ───────────────────────── */
.itm-kv {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.itm-kv > div {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 13px;
}
.itm-kv dt { color: var(--muted); }
.itm-kv dd { margin: 0; text-align: right; overflow-wrap: anywhere; }

/* ───────────────────────── Devices ───────────────────────── */
.itm-device__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.itm-dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  display: inline-block;
}
.itm-dot--ok {
  background: var(--ok);
  animation: itm-breathe-ok 2.6s ease-in-out infinite;
}
.itm-dot--down {
  background: var(--down);
  animation: itm-breathe-down 1.4s ease-in-out infinite;
}
@keyframes itm-breathe-ok {
  0%, 100% { box-shadow: 0 0 4px 0 rgba(57, 211, 83, 0.7); }
  50% { box-shadow: 0 0 10px 2px rgba(57, 211, 83, 0.35); }
}
@keyframes itm-breathe-down {
  0%, 100% { box-shadow: 0 0 4px 0 rgba(248, 81, 73, 0.8); opacity: 1; }
  50% { box-shadow: 0 0 12px 3px rgba(248, 81, 73, 0.4); opacity: 0.6; }
}
.itm-squares-title {
  margin: 14px 0 6px;
  font-size: 11px;
  color: var(--muted);
  font-weight: 500;
}

/* ───────────────────────── Disk bars ───────────────────────── */
.itm-disk { margin-bottom: 10px; }
.itm-disk__head {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 4px;
}
.itm-bar {
  height: 8px;
  background: var(--track);
  border-radius: 4px;
  overflow: hidden;
}
.itm-bar__fill {
  height: 100%;
  background: var(--ok);
  border-radius: 4px;
  transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.35s ease;
}
.itm-bar__fill--warn { background: var(--warn); }
.itm-bar__fill--down { background: var(--down); }

/* ───────────────────────── Table ───────────────────────── */
.itm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.itm-table th {
  text-align: left;
  color: var(--muted);
  font-weight: 500;
  padding: 6px 10px;
  border-bottom: 1px solid var(--border);
}
.itm-table td {
  padding: 6px 10px;
  border-bottom: 1px solid var(--border);
  transition: background-color 0.15s ease;
}
.itm-table tbody tr:hover td {
  background: color-mix(in srgb, var(--accent) 6%, transparent);
}
.itm-method {
  background: var(--track);
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 600;
}
.itm-duo {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}
.itm-slice-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;
}
.itm-slice .itm-table td {
  overflow-wrap: anywhere;
}
@media (max-width: 1100px) {
  .itm-duo { grid-template-columns: 1fr; }
}

/* ───────────────────────── Utilities ───────────────────────── */
.itm-mono { font-family: 'SF Mono', Menlo, Consolas, monospace; font-size: 12px; font-variant-numeric: tabular-nums; }
.itm-muted { color: var(--muted); font-size: 12px; }
.itm-center { text-align: center; margin-top: -6px; }
.itm-small { font-size: 11px; }
.itm-ok { color: var(--ok); }
.itm-warn { color: var(--warn); }
.itm-down { color: var(--down); }

@media (max-width: 1100px) {
  .itm-card { grid-column: span 6; }
  .itm-card--wide, .itm-card--full { grid-column: span 12; }
  .itm-grid--devices .itm-card { grid-column: span 6; }
}
@media (max-width: 700px) {
  .itm-card, .itm-grid--devices .itm-card { grid-column: span 12; }
}
@media (prefers-reduced-motion: reduce) {
  .itm-rise, .itm-live-dot--on, .itm-dot--ok, .itm-dot--down, .itm-tile--down, .itm-btn--spinning svg {
    animation: none;
  }
  .itm-card:hover, .itm-tile:hover, .itm-btn:hover {
    transform: none;
  }
}
</style>
